import { cx } from "@emotion/css";
import { Form, Formik, type FormikHelpers } from "formik";
import { Eye, FilePlus2, Folder, Pencil, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import type { DeckPermission } from "src/api/flashcardDecks";
import { CustomBadge } from "src/components/Default/CustomBadge";
import { BackButton } from "src/components/Default/LekUI/BackButton";
import {
	OutlinedTextBlackButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { TextField } from "src/components/Default/LekUI/TextField";
import Box from "src/components/Default/Main/Box";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { PermissionsFilters } from "src/components/NewComponents/Filters/FlashcardsFilters/PermissionFilter";
import { LayoutWithSearch } from "src/containers/Layouts/LayoutWithSearch";
import type { ImportedDeck } from "src/helpers/flashcards/ankiImport";
import { getMaxDecksInBatch } from "src/hooks/useAppConfig";
import { useCreateDecksBulk } from "src/hooks/useFlashcards";
import { isStaff } from "src/hooks/useUser";

interface ImportFormValues {
	permission: DeckPermission;
	folderIds: string[];
	decks: ImportedDeck[];
}

const initialValues: ImportFormValues = {
	permission: "public",
	folderIds: [],
	decks: [],
};

// Limit body na backendzie/nginx to 1MB — wysyłamy paczki z zapasem na permission/folderIds.
const MAX_CHUNK_BYTES = 800_000;

const jsonSize = (value: unknown) =>
	new TextEncoder().encode(JSON.stringify(value)).length;

// Kolejne zestawy w paczkach mieszczących się w MAX_CHUNK_BYTES (zachowuje kolejność).
const chunkDecks = <T,>(decks: T[]): T[][] =>
	decks.reduce<{ chunks: T[][]; size: number }>(
		(acc, deck) => {
			const size = jsonSize(deck);
			const last = acc.chunks[acc.chunks.length - 1];
			if (last && acc.size + size <= MAX_CHUNK_BYTES) {
				last.push(deck);
				acc.size += size;
			} else {
				acc.chunks.push([deck]);
				acc.size = size;
			}
			return acc;
		},
		{ chunks: [], size: 0 },
	).chunks;

export function FlashcardsDeckImportContainer() {
	const { t } = useTranslation();
	const history = useHistory();
	const { openModal } = useModals();
	const { mutateAsync: createDecksBulk, isPending: isLoading } =
		useCreateDecksBulk();
	// Staff importuje bez limitu — jak na backendzie (POST decks/bulk)
	const maxDecksInBatch = isStaff() ? Infinity : getMaxDecksInBatch();
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const defaultDeckName = t(
		"flashcardsImport.page.defaultDeckName",
		"Nowy zestaw",
	);

	const handleSubmit = async (
		values: ImportFormValues,
		{ setFieldValue }: FormikHelpers<ImportFormValues>,
	) => {
		if (values.decks.length > maxDecksInBatch) {
			toast.error(
				t(
					"flashcardsImport.page.maxDecks",
					"Możesz utworzyć maksymalnie {{count}} zestawów na raz",
					{ count: maxDecksInBatch },
				),
			);
			return;
		}
		const decks = values.decks.map((deck) => ({
			name: deck.name,
			// Limit backendu (Joi max 10000)
			description: deck.description?.slice(0, 10000),
			flashcards: deck.flashcards,
		}));
		const tooLarge = decks.find((deck) => jsonSize(deck) > MAX_CHUNK_BYTES);
		if (tooLarge) {
			toast.error(
				t(
					"flashcardsImport.page.deckTooLarge",
					"Zestaw „{{name}}” jest za duży — podziel go na mniejsze zestawy",
					{ name: tooLarge.name },
				),
			);
			return;
		}

		// Utworzone paczki zdejmujemy z formularza — ponowienie po błędzie wyśle tylko resztę.
		let remaining = values.decks;
		try {
			for (const chunk of chunkDecks(decks)) {
				await createDecksBulk({
					permission: values.permission,
					...(values.folderIds.length
						? { folderIds: values.folderIds.map(Number) }
						: {}),
					decks: chunk,
				});
				remaining = remaining.slice(chunk.length);
			}
			toast.success(
				t("flashcardsImport.page.success", "Zestawy zostały utworzone"),
			);
			history.push(routing.paths.userDecks);
		} catch {
			const created = values.decks.length - remaining.length;
			setFieldValue("decks", remaining);
			toast.error(
				created
					? t(
							"flashcardsImport.page.partialError",
							"Utworzono {{count}} zestawów, pozostałych nie udało się utworzyć — spróbuj ponownie",
							{ count: created },
						)
					: t("flashcardsImport.page.error", "Nie udało się utworzyć zestawów"),
			);
		}
	};

	return (
		<LayoutWithSearch>
			<Box
				className="creator--box"
				Header={
					<div className="flex gap-8 items-center">
						<BackButton />
						<FilePlus2 className="icon-super-big icon-purple" />
						<h2 className="h2 m-0">
							{t("flashcardsImport.page.title", "Importuj zestawy fiszek")}
						</h2>
						<span
							className={cx(
								"purple-box size-12 py-4 px-8 border-radius-8 w-fit-content",
							)}
						>
							{t("flashcardsImport.page.faq", "FAQ")}
						</span>
					</div>
				}
			>
				<Formik initialValues={initialValues} onSubmit={handleSubmit}>
					{({ values, setFieldValue }) => {
						const isEmpty = values.decks.length === 0;

						const openImportModal = () =>
							openModal(ModalsNames.DecksImportModal, {
								title: t("creators.decks.importDecks", "Importuj zestawy"),
								onConfirmDecks: (decks: ImportedDeck[]) => {
									const combined = [
										...values.decks,
										...decks.map((deck) => ({
											name: deck.name || defaultDeckName,
											description: deck.description,
											flashcards: deck.flashcards,
										})),
									];
									if (combined.length > maxDecksInBatch) {
										toast.warning(
											t(
												"flashcardsImport.page.maxDecks",
												"Możesz utworzyć maksymalnie {{count}} zestawów na raz",
												{ count: maxDecksInBatch },
											),
										);
									}
									setFieldValue("decks", combined.slice(0, maxDecksInBatch));
								},
							});

						const openFolderModal = () =>
							openModal(ModalsNames.SelectFoldersModal, {
								initialSelectedIds: values.folderIds,
								onSelect: (ids: string[]) => setFieldValue("folderIds", ids),
							});

						return (
							<Form className="creator">
								<div className="flex-1 flex flex-col gap-16">
									<div className="flex flex-col gap-8">
										<span className="size-16-light">
											{t("creators.decks.importDecks", "Importuj zestawy")}
										</span>
										<div className="flex justify-between items-center gap-8 flex-wrap">
											<OutlinedTextBlackButton
												className="flex items-center gap-4"
												onClick={openImportModal}
												disabled={isEmpty}
												data-test-id="import-decks-button"
											>
												<span>{t("commands.import", "Importuj")}</span>
												<Upload className="icon-super-small" />
											</OutlinedTextBlackButton>
											<OutlinedTextBlackButton
												data-test-id="import-select-folders-button"
												onClick={openFolderModal}
												disabled={isEmpty}
												className="flex items-center gap-8 w-fit-content creator--folder-button"
											>
												<div className="creator--folder-icon-wrapper">
													<Folder className="icon-small" />
													{values.folderIds.length > 0 && (
														<CustomBadge
															count={values.folderIds.length}
															maxCount={99}
															className="creator--folder-button-badge"
														/>
													)}
												</div>
												<span className="creator--folder-button-text">
													{values.folderIds.length > 0 ? (
														<>
															{t("creators.foldersSelectedLabel", "Foldery:")}{" "}
															<strong>{values.folderIds.length}</strong>
														</>
													) : (
														t("creators.selectFolders", "Dodaj do folderów")
													)}
												</span>
											</OutlinedTextBlackButton>
										</div>
									</div>

									<div className="flex flex-col gap-8">
										<span className="size-16-light">
											{t(
												"filters.decks.permission.title",
												"Dostępność zestawu",
											)}
										</span>
										<PermissionsFilters
											className="imported-deck-permissions"
											permission={values.permission}
											setPermission={(permission) =>
												setFieldValue("permission", permission)
											}
											disabled={isEmpty}
										/>
									</div>

									<span
										className="size-18-light"
										data-test-id="imported-decks-count"
									>
										{t(
											"flashcardsImport.page.importedCount",
											"Zaimportowane zestawy",
										)}
										: {values.decks.length}
									</span>

									{values.decks.length === 0 ? (
										<div className="flex flex-col items-center justify-center text-center gap-8 border-medium-grey border-radius-16 bg-white py-48 px-24">
											<span className="size-20">
												{t(
													"flashcardsImport.page.emptyTitle",
													"Nie masz dodanych żadnych zestawów",
												)}
											</span>
											<span className="size-16-light text-grey">
												{t(
													"flashcardsImport.page.emptySubtitle",
													"Użyj przycisku poniżej i wgraj zestawy fiszek. Dodane pliki pojawią się w tym miejscu",
												)}
											</span>
											<PrimaryButton
												className="mt-16"
												onClick={openImportModal}
												data-test-id="import-decks-empty-button"
											>
												{t("creators.decks.importDecks", "Importuj zestawy")}
											</PrimaryButton>
										</div>
									) : (
										<div className="flex flex-col gap-12">
											{values.decks.map((deck, i) => (
												<div
													key={i}
													className="imported-deck p-16 border-radius-12 bg"
												>
													<div className="imported-deck--name">
														{editingIndex === i ? (
															<TextField
																autoFocus
																fullWidth
																className="max-w-400"
																variant="outlined"
																size="small"
																value={deck.name}
																onChange={(e) =>
																	setFieldValue(
																		`decks.${i}.name`,
																		e.target.value,
																	)
																}
																onBlur={() => {
																	if (!deck.name.trim()) {
																		setFieldValue(
																			`decks.${i}.name`,
																			defaultDeckName,
																		);
																	}
																	setEditingIndex(null);
																}}
																onKeyDown={(e) => {
																	if (e.key === "Enter") e.currentTarget.blur();
																}}
																data-test-id="imported-deck-name-input"
															/>
														) : (
															<span
																className="size-18 text-ellipsis-1"
																title={deck.name}
															>
																{deck.name}
															</span>
														)}
													</div>
													<span className="size-18-light">
														{t(
															"creators.decks.flashcardsCount",
															"Ilość fiszek",
														)}
														: {deck.flashcards.length}
													</span>
													<div className="imported-deck--actions">
														<button
															type="button"
															className="soft-icon-button soft-icon-button--error"
															onClick={() =>
																setFieldValue(
																	"decks",
																	values.decks.filter((_, idx) => idx !== i),
																)
															}
															data-test-id="imported-deck-delete"
														>
															<Trash2 className="icon-small icon-error" />
														</button>
														<button
															type="button"
															className="soft-icon-button soft-icon-button--primary"
															onClick={() => setEditingIndex(i)}
															data-test-id="imported-deck-edit-name"
														>
															<Pencil className="icon-small icon-primary" />
														</button>
														<button
															type="button"
															className="soft-icon-button soft-icon-button--primary"
															onClick={() =>
																openModal(ModalsNames.DeckPreviewModal, {
																	deckName: deck.name,
																	flashcards: deck.flashcards,
																	hideStudyButton: true,
																})
															}
															data-test-id="imported-deck-preview"
														>
															<Eye className="icon-small icon-primary" />
														</button>
													</div>
												</div>
											))}
										</div>
									)}
								</div>

								<div className="sticky-bottom">
									<PrimaryButton
										type="submit"
										className="w-100"
										isLoading={isLoading}
										disabled={isLoading || values.decks.length === 0}
										data-test-id="import-create-decks-button"
									>
										{t("flashcardsImport.page.createDecks", "Utwórz zestawy")}
									</PrimaryButton>
								</div>
							</Form>
						);
					}}
				</Formik>
			</Box>
		</LayoutWithSearch>
	);
}
